import { useState } from "react";
import { useLocation } from "react-router-dom";
import { CheckCircle2, HelpCircle, Lightbulb, Loader2, X } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const TIPS = {
  office: [
    "Trips → New trip builds a manifest and prices it from your vehicle rules.",
    "Dispatch → Live dispatch shows every car moving, plus the arrivals board.",
    "Settings → Operations holds your wait-time grace, change fees and no-show policy.",
    "Portals & team is where you invite dispatchers, chauffeurs and passengers.",
  ],
  driver: [
    "Tap the big button to move the trip along — accept, on my way, I'm here, start, finish.",
    "Share GPS lets your passenger watch you arrive and starts your wait timer.",
    "Once you have arrived and waited, the no-show button appears if nobody shows.",
    "Earnings shows what you have made and your monthly statement.",
  ],
  client: [
    "Book a trip gives you the price before you confirm anything.",
    "Track my ride shows your chauffeur on the map with a live arrival time.",
    "Add stop asks your operator to add a stop mid-trip.",
    "Need a change? Edit or cancel from your trip inside your operator's window.",
  ],
};

const TABS = [
  { key: "help", label: "Get help", icon: HelpCircle, kind: "help",
    placeholder: "What are you stuck on? The more detail, the faster we can help.",
    cta: "Send to support",
    done: "Sent. Support will get back to you by email." },
  { key: "suggestion", label: "Suggest an idea", icon: Lightbulb, kind: "suggestion",
    placeholder: "What would make this app better for you?",
    cta: "Send suggestion",
    done: "Thank you — your idea went straight to the team." },
];

/** Floating help + suggestion launcher. Rendered once per app. */
export const HelpButton = ({ app = "office" }) => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("help");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState("");

  const active = TABS.find((t) => t.key === tab);

  const close = () => {
    setOpen(false);
    setSent("");
    setError("");
    setMessage("");
  };

  const send = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post("/feedback", { kind: active.kind, message, page: pathname });
      setSent(active.done);
      setMessage("");
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button type="button" data-testid="help-launcher" onClick={() => setOpen(true)}
        aria-label="Help and suggestions"
        className="fixed bottom-4 right-4 z-40 flex h-12 w-12 items-center justify-center gap-2 rounded-full border border-gold/40 bg-ink-800/95 text-sm text-gold shadow-xl backdrop-blur transition-colors hover:border-gold hover:bg-ink-700 sm:bottom-5 sm:right-5 sm:w-auto sm:px-4">
        <HelpCircle className="h-4 w-4" /> <span className="hidden sm:inline">Help</span>
      </button>
    );
  }

  return (
    <div data-testid="help-panel"
      className="fixed inset-x-4 bottom-4 z-[60] max-h-[80vh] overflow-y-auto rounded-2xl border border-ink-600 bg-ink-800/98 p-5 shadow-2xl backdrop-blur sm:inset-x-auto sm:right-5 sm:w-[26rem]">
      <div className="flex items-start justify-between gap-3">
        <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Help &amp; suggestions</p>
        <button type="button" data-testid="help-close" onClick={close} aria-label="Close help"
          className="text-slate-500 hover:text-slate-200">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-4 flex gap-2">
        {TABS.map((t) => (
          <button key={t.key} type="button" data-testid={`help-tab-${t.key}`}
            onClick={() => { setTab(t.key); setSent(""); setError(""); }}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-xs transition-colors ${
              tab === t.key ? "border-gold/50 bg-gold/10 text-gold"
                : "border-ink-600 text-slate-400 hover:text-slate-200"}`}>
            <t.icon className="h-3.5 w-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "help" && (
        <ul className="mt-4 space-y-2">
          {(TIPS[app] || []).map((tip) => (
            <li key={tip} className="flex gap-2 text-xs leading-relaxed text-slate-400">
              <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gold" /> {tip}
            </li>
          ))}
        </ul>
      )}

      {sent ? (
        <p data-testid="help-sent" className="mt-4 flex items-start gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-300">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> {sent}
        </p>
      ) : (
        <>
          <Textarea data-testid="help-message" rows={4} value={message}
            placeholder={active.placeholder}
            onChange={(e) => setMessage(e.target.value)}
            className="mt-4 border-ink-600 bg-ink-900 text-sm text-slate-100 focus-visible:ring-gold" />
          {error && <p data-testid="help-error" className="mt-2 text-xs text-red-400">{error}</p>}
          <Button data-testid="help-submit" disabled={busy || message.trim().length < 4} onClick={send}
            className="mt-3 h-10 w-full bg-gold text-sm text-ink-900 hover:bg-gold-light">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : active.cta}
          </Button>
        </>
      )}
    </div>
  );
};
